import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, IconButton, Box } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import designTokens from '../../styles/designTokens';

interface LoginRequiredPopupProps {
  open: boolean;
  onCancel: () => void;
  onGoLogin: () => void;
}

export default function LoginRequiredPopup({ open, onCancel, onGoLogin }: LoginRequiredPopupProps) {
  const loginRequiredDialog = designTokens.semantic.dashboard.loginRequiredDialog;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: loginRequiredDialog.surface.width,
          minHeight: loginRequiredDialog.surface.minHeight,
          borderRadius: loginRequiredDialog.surface.radius,
          backgroundColor: loginRequiredDialog.surface.background,
          color: loginRequiredDialog.surface.text,
          overflow: loginRequiredDialog.surface.overflow,
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: loginRequiredDialog.surface.backdrop,
        }
      }}
    >
      {/* Top Bar */}
      <Box sx={{ height: loginRequiredDialog.spacer.height }} />
      
      {/* Header */}
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        gap: loginRequiredDialog.header.gap,
        height: loginRequiredDialog.header.height,
        px: loginRequiredDialog.header.paddingX,
        py: loginRequiredDialog.header.paddingY,
      }}>
        <Typography sx={{ 
          ...designTokens.typography.styles.heading,
          color: loginRequiredDialog.surface.text,
          flex: 1,
        }}>
          Sign-in required
        </Typography>
        <IconButton 
          size="small" 
          onClick={onCancel}
          sx={{
            width: loginRequiredDialog.closeButton.size,
            height: loginRequiredDialog.closeButton.size,
            // 아이콘이 박스와 같은 24라 MUI small의 기본 padding이 남으면 넘친다.
            p: 0,
            color: loginRequiredDialog.closeButton.color,
            '&:hover': {
              backgroundColor: loginRequiredDialog.closeButton.hoverBackground,
            },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: loginRequiredDialog.closeButton.iconSize }} />
        </IconButton>
      </DialogTitle>
      
      {/* Description */}
      <DialogContent sx={{ 
        flex: 1,
        minHeight: loginRequiredDialog.body.minHeight,
        px: loginRequiredDialog.body.paddingX,
        py: loginRequiredDialog.body.paddingY,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: loginRequiredDialog.body.messageGap,
        }}>
          <Typography sx={{ 
            ...designTokens.typography.styles.headline1,
            color: loginRequiredDialog.body.text,
            mb: 0,
          }}>
            This feature is available after signing in.
          </Typography>
          <Typography sx={{ 
            ...designTokens.typography.styles.headline1,
            color: loginRequiredDialog.body.text,
          }}>
            Please connect your account to continue using our service.
          </Typography>
        </Box>
      </DialogContent>
      
      {/* Buttons */}
      <DialogActions sx={{ 
        height: loginRequiredDialog.actions.height,
        px: loginRequiredDialog.actions.paddingX,
        py: loginRequiredDialog.actions.paddingY,
        gap: loginRequiredDialog.actions.gap,
        justifyContent: 'flex-end',
      }}>
        <Button 
          onClick={onCancel} 
          variant="outlined"
          sx={{
            textTransform: 'none',
            minWidth: loginRequiredDialog.actionButton.secondary.minWidth,
            height: loginRequiredDialog.actionButton.secondary.height,
            pl: loginRequiredDialog.actionButton.secondary.paddingLeft,
            pr: loginRequiredDialog.actionButton.secondary.paddingRight,
            py: loginRequiredDialog.actionButton.secondary.paddingY,
            borderRadius: loginRequiredDialog.actionButton.secondary.radius,
            borderColor: loginRequiredDialog.actionButton.secondary.border,
            color: loginRequiredDialog.actionButton.secondary.text,
            ...designTokens.typography.styles.body,
            '&:hover': {
              borderColor: loginRequiredDialog.actionButton.secondary.border,
              backgroundColor:
                loginRequiredDialog.actionButton.secondary.hoverBackground,
            },
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={onGoLogin} 
          variant="contained"
          sx={{
            textTransform: 'none',
            minWidth: loginRequiredDialog.actionButton.primary.minWidth,
            height: loginRequiredDialog.actionButton.primary.height,
            pl: loginRequiredDialog.actionButton.primary.paddingLeft,
            pr: loginRequiredDialog.actionButton.primary.paddingRight,
            py: loginRequiredDialog.actionButton.primary.paddingY,
            borderRadius: loginRequiredDialog.actionButton.primary.radius,
            backgroundColor: loginRequiredDialog.actionButton.primary.background,
            color: loginRequiredDialog.actionButton.primary.text,
            ...designTokens.typography.styles.body,
            '&:hover': {
              backgroundColor:
                loginRequiredDialog.actionButton.primary.hoverBackground,
            },
          }}
        >
          Sign in
        </Button>
      </DialogActions>
      
      {/* Bottom Bar */}
      <Box sx={{ height: loginRequiredDialog.spacer.height }} />
    </Dialog>
  );
}
